/**
 * 套团 · 新建产品第 1 步的纯逻辑（TAOTUAN-4）——不依赖 React，便于 vitest。
 *
 * 红线：这里不做任何余位 / 座位 / 天数推算。上车天 / 下车天是用户点选的值；
 * 行程骨架只是把接口返回的线路逐日（route-lines[].days）按所选区间切出来展示，
 * 行程日的顺序与后端 effective_day_logistics 一致（[接机日] + 核心天 + [送机日]）。
 */
import type { ConditionalRule, DropoffMode, PickupMode } from "@/services/types/products";
import type { RouteLine, RouteLineDay } from "@/services/types/routeConfig";

/** 用户 10-09 定：接机 / 送机每趟默认 $60（±$10 同事按实际改）。只用来**预填输入框**——看得见、可改、保存时仍必填；存量产品不自动写。 */
export const DEFAULT_TRANSFER_TRIP_COST_CENTS = 6000;

// ── 上 / 下车天点选 ─────────────────────────────────────────────────────────
export interface DayPick {
  board: number | null;
  alight: number | null;
  /** true = 已点了上车天、下一次点击是下车天 */
  awaitingAlight: boolean;
}
export const EMPTY_PICK: DayPick = { board: null, alight: null, awaitingAlight: false };

/**
 * 先点上车天、再点下车天；点到上车天之前自动对调；超出线路天数（total）的点击忽略。
 * total 来自接口（route-lines[].total_days），不是前端算的。
 */
export function clickDay(pick: DayPick, day: number, total: number): DayPick {
  if (!Number.isInteger(day) || day < 1 || day > total) return pick;
  if (!pick.awaitingAlight) return { board: day, alight: day, awaitingAlight: true };
  if (pick.board != null && day < pick.board) return { board: day, alight: pick.board, awaitingAlight: false };
  return { board: pick.board, alight: day, awaitingAlight: false };
}

// ── 行程骨架（只读展示）─────────────────────────────────────────────────────
export type SkeletonRow =
  | { kind: "transfer_pickup" }                       // 接机 + 住一晚（算第 1 天；酒店由后端取值函数给，见 getPickupNightHotel）
  | { kind: "core"; day: RouteLineDay; bigCarDay: number }
  | { kind: "transfer_dropoff" };

/**
 * 行程骨架：[接机日（仅「接机 + 住一晚」）] + 大车天 + [送机日（含送机）]——和后端 transfer_modes.trip_days 同一顺序。
 * 单接机不算行程天（没有这一行）。前端只摆行，不算酒店 / 座位。
 */
export function buildSkeleton(line: RouteLine | undefined, board: number | null, alight: number | null, pickup: PickupMode, dropoff: DropoffMode): SkeletonRow[] {
  if (!line || board == null || alight == null) return [];
  const rows: SkeletonRow[] = [];
  if (pickup === "stay") rows.push({ kind: "transfer_pickup" });
  for (const d of line.days) if (d.line_day_no >= board && d.line_day_no <= alight) rows.push({ kind: "core", day: d, bigCarDay: d.line_day_no - board + 1 });
  if (dropoff === "day") rows.push({ kind: "transfer_dropoff" });
  return rows;
}

/** 行程天数 / 晚数 / 坐大车天数（系统按选择算，不手填）：N 天 M 晚，晚数 = 天数 − 1。 */
export function tripSummary(rows: SkeletonRow[]): { days: number; nights: number; bigCarDays: number } {
  const days = rows.length;
  return { days, nights: Math.max(0, days - 1), bigCarDays: rows.filter((r) => r.kind === "core").length };
}

// ── 第 1 步的套团状态 ───────────────────────────────────────────────────────
export interface TaotuanState {
  enabled: boolean;
  routeLineId: string;
  pick: DayPick;
  vehicleTier: string;
  pickupMode: PickupMode;
  dropoffMode: DropoffMode;
  /** 个别团型改「接机当晚酒店」（空 = 用默认，默认由后端按线路 / 母线取） */
  pickupHotelId: string;
  /** TRANSCOST-1 · 本产品自己填的接机 / 送机成本（美分·每趟）；null = 没填（线路有默认就继承，没有就必填）。选接机 / 送机时没默认 → 预填 $60（可改） */
  pickupCostCents: number | null;
  dropoffCostCents: number | null;
  condEnabled: boolean;
  condDays: number[];        // 星期，同 weekly_cadence_json.days_of_week（0=周日…6=周六）
  condLineId: string;
  condBoardDay: number | null;
}

export const EMPTY_TAOTUAN: TaotuanState = {
  enabled: false, routeLineId: "", pick: EMPTY_PICK, vehicleTier: "", pickupMode: "none", dropoffMode: "none", pickupHotelId: "",
  pickupCostCents: null, dropoffCostCents: null,
  condEnabled: false, condDays: [], condLineId: "", condBoardDay: null,
};

export const VEHICLE_TIERS = [
  { value: "A13", label: "13 座（A）" },
  { value: "B10", label: "10 座（B）" },
  { value: "C11", label: "11 座头等舱（C）" },
  { value: "D14", label: "14 座（D）" },   // VEHTIER-14 · 拉斯维加斯 14 座车
] as const;

export const PICKUP_OPTIONS: { value: PickupMode; label: string }[] = [
  { value: "none", label: "不含" },
  { value: "service", label: "单接机" },
  { value: "stay", label: "接机 + 住一晚" },
];
export const DROPOFF_OPTIONS: { value: DropoffMode; label: string }[] = [
  { value: "none", label: "不含" },
  { value: "day", label: "含送机" },
];
export const PICKUP_LABEL: Record<PickupMode, string> = { none: "不含接机", service: "单接机", stay: "接机 + 住一晚" };
export const DROPOFF_LABEL: Record<DropoffMode, string> = { none: "不含送机", day: "含送机" };
export const PICKUP_SOURCE_LABEL: Record<string, string> = {
  product: "团型自己选的", route_day: "来自母线上车前一天的住宿", line_start: "来自线路「接机当晚默认酒店」", unset: "未配",
};

/**
 * TRANSCOST-1 · 选了接机 / 送机而成本没有来源（本产品没填 + 线路也没默认）→ 返回缺的项（["接机","送机"] 的子集）。
 * 取值规则和后端 transfer_modes.resolve_transfer_cost 一致：产品填的 → 线路默认 → 未填。
 */
export function missingTransferCosts(t: TaotuanState, line?: LineCostSource): string[] {
  const out: string[] = [];
  if (t.pickupMode !== "none" && !t.pickupCostCents && !lineHasCost(line, "pickup")) out.push("接机");
  if (t.dropoffMode !== "none" && !t.dropoffCostCents && !lineHasCost(line, "dropoff")) out.push("送机");
  return out;
}

/** 线路里和「接 / 送机成本有没有来源」有关的字段：`*_cost_set` 是不含金额的是 / 否（所有角色都有），金额只有成本可见角色才有。 */
export type LineCostSource = Pick<RouteLine, "pickup_cost_cents" | "dropoff_cost_cents"> & Partial<Pick<RouteLine, "pickup_cost_set" | "dropoff_cost_set">>;

/**
 * TRANSCOST-3 · 线路配没配默认成本：以后端给的 `*_cost_set` 为准（非成本可见角色读不到金额，只能靠它）；
 * 没有这个字段（旧后端 / 旧数据）才退回看金额。和后端同口径：非 0 才算配了。
 */
export function lineHasCost(line: LineCostSource | undefined, which: "pickup" | "dropoff"): boolean {
  if (!line) return false;
  const set = which === "pickup" ? line.pickup_cost_set : line.dropoff_cost_set;
  if (typeof set === "boolean") return set;
  return !!(which === "pickup" ? line.pickup_cost_cents : line.dropoff_cost_cents);
}

/** 选接机 / 送机时没有来源 → 预填的每趟成本（线路有默认就继承，返回 null 不预填）。 */
export function prefillCost(current: number | null, lineDefault: number | null | undefined): number | null {
  return current ?? (lineDefault ? null : DEFAULT_TRANSFER_TRIP_COST_CENTS);
}

export function isTaotuanComplete(t: TaotuanState, line?: LineCostSource, checkCost = false): boolean {
  if (!t.enabled) return true;
  // 选了接机 / 送机就必须有成本（只有成本可见角色在界面填；其它角色交给后端 422 把关，checkCost=false）
  if (checkCost && missingTransferCosts(t, line).length > 0) return false;
  // 车档必填（用户 10-01 追加：后端对缺车档返回 400）
  if (!t.routeLineId || t.pick.board == null || t.pick.alight == null || !t.vehicleTier) return false;
  if (t.condEnabled && (t.condDays.length === 0 || !t.condLineId || t.condBoardDay == null)) return false;
  return true;
}

/** 提交 POST /products 时附加的套团字段；不套团 = 空对象（payload 与改前逐字节一致）。 */
export function taotuanCreateFields(t: TaotuanState): {
  route_line_id?: string; board_day?: number; alight_day?: number; vehicle_tier?: string;
  pickup_mode?: PickupMode; dropoff_mode?: DropoffMode; pickup_hotel_id?: string | null;
  pickup_cost_cents?: number | null; dropoff_cost_cents?: number | null; conditional_rule?: ConditionalRule | null;
} {
  if (!t.enabled || !t.routeLineId || t.pick.board == null || t.pick.alight == null) return {};
  return {
    route_line_id: t.routeLineId,
    board_day: t.pick.board,
    alight_day: t.pick.alight,
    vehicle_tier: t.vehicleTier,
    pickup_mode: t.pickupMode,
    dropoff_mode: t.dropoffMode,
    pickup_hotel_id: t.pickupMode === "stay" && t.pickupHotelId ? t.pickupHotelId : null,
    // TRANSCOST-1 · 没选接机 / 送机就不带成本；null = 继承线路默认
    pickup_cost_cents: t.pickupMode !== "none" ? t.pickupCostCents : null,
    dropoff_cost_cents: t.dropoffMode !== "none" ? t.dropoffCostCents : null,
    conditional_rule: t.condEnabled && t.condLineId && t.condBoardDay != null
      ? { days: t.condDays, depends_on_route_line_id: t.condLineId, depends_on_board_day: t.condBoardDay }
      : null,
  };
}

// ── 行程日编辑：挂了线路的产品，这三个字段来自路段、只读 ─────────────────────
export const ROUTE_LOCKED_LEG_FIELDS = ["default_hotel_id", "meeting_time", "pickup_location"] as const;

/**
 * 只取「改动过」的字段（与加载时的快照比）；挂线路产品的锁定字段一律不带——后端
 * PATCH 本来就会拒绝这三个字段，前端从源头不提交。cur 里值为 undefined 的键视为
 * 「没填」，与原有 dayToPayload 的 `|| undefined` 语义一致（不提交）。
 */
export function diffLegPayload<T extends object>(orig: T, cur: T, locked: readonly string[] = ROUTE_LOCKED_LEG_FIELDS): Partial<T> {
  const o = orig as Record<string, unknown>;
  const c = cur as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(c)) {
    if (locked.includes(k)) continue;
    if (c[k] === undefined) continue;
    if (JSON.stringify(c[k]) !== JSON.stringify(o[k])) out[k] = c[k];
  }
  return out as Partial<T>;
}

export const hhmm = (t: string | null | undefined): string => (t ? t.slice(0, 5) : "");
